import { FiLayout } from 'react-icons/fi';

export const sentenceStructureData = {
  courseId: 'sentence-structure',
  courseType: 'grammar',
  title: 'Sentence Structure',
  icon: FiLayout,
  overview: {
    impact: 'Foundation for all IELTS writing and speaking',
    focus: 'Building grammatically correct sentences',
    uses: 'All IELTS sections - Writing, Speaking, Reading',
    duration: '20-25 minutes',
  },
  sections: [
    {
      title: 'Basic Sentence Structure',
      content: `<p>Every complete English sentence needs at least a <strong>subject</strong> and a <strong>verb</strong>. Understanding this foundation is essential for IELTS success.</p>
      <h4>The Core Pattern: S + V + O</h4>
      <p>Subject + Verb + Object is the most common English sentence pattern.</p>`,
      examples: [
        { label: 'Simple', text: 'She writes.' },
        { label: 'S + V + O', text: 'She writes essays.' },
        { label: 'Extended', text: 'She writes excellent essays every day.' },
      ],
    },
    {
      title: 'Types of Sentences',
      content: `<p>IELTS rewards candidates who use a variety of sentence types. Here are the four main types:</p>`,
      table: {
        headers: ['Type', 'Structure', 'Example'],
        rows: [
          ['Simple', 'One independent clause', 'Technology has changed our lives.'],
          ['Compound', 'Two independent clauses with coordinator', 'Technology has improved, but challenges remain.'],
          ['Complex', 'Independent + dependent clause', 'Although technology has improved, challenges remain.'],
          ['Compound-Complex', 'Multiple clauses', 'Technology has improved, and while it brings benefits, challenges remain.'],
        ],
      },
    },
    {
      title: 'Word Order Rules',
      content: `<p>English has strict word order rules. Getting these wrong can confuse readers and hurt your IELTS score.</p>`,
      points: [
        'Adjectives come before nouns: "a beautiful garden" (not "a garden beautiful")',
        'Adverbs of frequency come before main verbs: "She always arrives on time"',
        'Time expressions usually go at the end: "I studied English yesterday"',
        'Place comes before time: "I met her at the cafe yesterday"',
      ],
      warning: 'Incorrect word order is a common error that affects your Grammar score in IELTS.',
    },
    {
      title: 'Sentence Variety for IELTS',
      content: `<p>Using different sentence structures shows grammatical range, which is crucial for Band 7+.</p>`,
      points: [
        'Start some sentences with adverbs: "Interestingly, the data shows..."',
        'Use introductory phrases: "In recent years, technology has..."',
        'Vary sentence length: mix short punchy sentences with longer complex ones',
        'Use different clause types: relative, conditional, purpose clauses',
      ],
    },
  ],
  exercises: [
    {
      title: 'Identify Sentence Types',
      type: 'select',
      instructions: 'Identify whether each sentence is Simple, Compound, or Complex.',
      questions: [
        {
          text: 'Technology has transformed modern education.',
          options: ['Simple', 'Compound', 'Complex'],
          answer: 'Simple',
          explanation: 'This has one independent clause with no conjunctions or subordinating words.',
        },
        {
          text: 'Technology has transformed education, and students now learn differently.',
          options: ['Simple', 'Compound', 'Complex'],
          answer: 'Compound',
          explanation: 'Two independent clauses joined by "and" (coordinating conjunction).',
        },
        {
          text: 'Although technology has benefits, it also creates challenges.',
          options: ['Simple', 'Compound', 'Complex'],
          answer: 'Complex',
          explanation: '"Although" introduces a dependent clause, followed by an independent clause.',
        },
        {
          text: 'The internet connects people worldwide.',
          options: ['Simple', 'Compound', 'Complex'],
          answer: 'Simple',
          explanation: 'One subject (internet), one verb (connects), one complete thought.',
        },
        {
          text: 'She studied hard, but she failed the exam.',
          options: ['Compound', 'Simple', 'Complex'],
          answer: 'Compound',
          explanation: 'Two independent clauses joined by "but".',
        },
        {
          text: 'Because the weather was cold, we stayed home.',
          options: ['Complex', 'Compound', 'Simple'],
          answer: 'Complex',
          explanation: '"Because" introduces a dependent clause.',
        },
      ],
    },
    {
      title: 'Identify Sentence Components',
      type: 'select',
      instructions: 'What is the role of the underlined word?',
      questions: [
        {
          text: 'In "The students completed the assignment," what is "The students"?',
          options: ['Subject', 'Verb', 'Object'],
          answer: 'Subject',
          explanation: 'The subject performs the action.',
        },
        {
          text: 'In "The teacher graded the essays," what is "graded"?',
          options: ['Verb', 'Subject', 'Object'],
          answer: 'Verb',
          explanation: 'The verb is the action word.',
        },
        {
          text: 'In "She reads books every day," what is "books"?',
          options: ['Object', 'Subject', 'Verb'],
          answer: 'Object',
          explanation: 'The object receives the action of the verb.',
        },
      ],
    },
    {
      title: 'Fix Word Order',
      type: 'fill-in',
      instructions: 'Rewrite the sentence with correct word order.',
      questions: [
        {
          text: 'Wrong: "She bought yesterday a dress new."',
          answer: 'She bought a new dress yesterday.',
          explanation: 'Adjective (new) before noun (dress), time (yesterday) at the end.',
        },
        {
          text: 'Wrong: "Always I study in the morning English."',
          answer: 'I always study English in the morning.',
          explanation: 'Frequency adverb before main verb, object after verb, time at end.',
        },
        {
          text: 'Wrong: "He speaks well very English."',
          answer: 'He speaks English very well.',
          explanation: 'Object (English) comes before adverb phrase (very well).',
        },
        {
          text: 'Wrong: "In the park was playing a child."',
          answer: 'A child was playing in the park.',
          explanation: 'Subject before verb, place at the end.',
        },
      ],
    },
    {
      title: 'Combine Sentences',
      type: 'writing',
      instructions: 'Combine the simple sentences into compound or complex sentences.',
      questions: [
        {
          text: 'Combine: "She studied hard. She passed the exam."',
          example: 'Because she studied hard, she passed the exam. / She studied hard, so she passed the exam.',
          tip: 'Use "because" for complex, or "so/and" for compound.',
        },
        {
          text: 'Combine: "The weather was cold. We went outside."',
          example: 'Although the weather was cold, we went outside. / The weather was cold, but we went outside.',
          tip: 'Use "although/even though" or "but" to show contrast.',
        },
        {
          text: 'Combine: "He finished his homework. He watched TV."',
          example: 'After he finished his homework, he watched TV. / He finished his homework, and then he watched TV.',
          tip: 'Use "after/when" for time relationship.',
        },
      ],
    },
  ],
  tips: [
    'Master the basic S + V + O pattern before adding complexity',
    'Use a variety of sentence types in your writing',
    'Check word order carefully when proofreading',
    'Practice combining simple sentences into complex ones',
  ],
  dailyPractice: [
    'Identify sentence types in IELTS reading passages',
    'Rewrite simple sentences as complex sentences',
    'Practice starting sentences in different ways',
    'Read your writing aloud to check natural flow',
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar Concepts',
  aiContext: 'Sentence Structure: The foundation of English grammar covering S+V+O patterns, sentence types (simple, compound, complex), and word order rules essential for IELTS writing.',
};

// Difficulty-variant factory: returns content adapted to the given difficulty level.
// difficulty 1-2: simplified content with basic examples and easier exercises
// difficulty 3: base content (default)
// difficulty 4-5: advanced content with complex examples and harder exercises
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...sentenceStructureData,
      title: sentenceStructureData.title + ' (Foundations)',
      overview: {
        ...sentenceStructureData.overview,
        duration: '15-20 minutes',
      },
      // Simplified: show only first 2 sections and first exercise set
      sections: sentenceStructureData.sections.slice(0, Math.max(2, Math.ceil(sentenceStructureData.sections.length / 2))),
      exercises: sentenceStructureData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...sentenceStructureData,
      title: sentenceStructureData.title + ' (Advanced)',
      overview: {
        ...sentenceStructureData.overview,
        duration: '30-35 minutes',
      },
      // Advanced: all sections plus advanced tips context
      sections: sentenceStructureData.sections,
      exercises: sentenceStructureData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(sentenceStructureData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return sentenceStructureData;
}
